import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface MarkdownViewProps {
  value: string;
  className?: string;
}

export function MarkdownView({ value, className }: MarkdownViewProps) {
  return (
    <div
      className={
        className ??
        'markdown-view rounded-lg border border-gray-700 bg-surface-900 p-3 text-sm leading-6 text-gray-300'
      }
    >
      <Markdown
        remarkPlugins={[remarkGfm]}
        components={{
          h1: (props) => <h1 className="mt-4 mb-2 text-lg font-semibold text-gray-100" {...props} />,
          h2: (props) => <h2 className="mt-4 mb-2 text-base font-semibold text-gray-100" {...props} />,
          h3: (props) => <h3 className="mt-3 mb-1 text-sm font-semibold text-gray-100" {...props} />,
          h4: (props) => <h4 className="mt-3 mb-1 text-sm font-semibold text-gray-200" {...props} />,
          p: (props) => <p className="my-2" {...props} />,
          ul: (props) => <ul className="my-2 list-disc pl-5" {...props} />,
          ol: (props) => <ol className="my-2 list-decimal pl-5" {...props} />,
          li: (props) => <li className="my-0.5" {...props} />,
          a: (props) => (
            <a
              className="text-primary-400 underline underline-offset-2 hover:text-primary-300"
              target="_blank"
              rel="noreferrer"
              {...props}
            />
          ),
          blockquote: (props) => (
            <blockquote className="my-2 border-l-2 border-gray-600 pl-3 text-gray-400" {...props} />
          ),
          hr: () => <hr className="my-4 border-gray-700" />,
          code: ({ className: cls, children, ...rest }) => {
            const isBlock = /language-/.test(cls ?? '');
            if (isBlock) {
              return (
                <code className={`${cls ?? ''} font-mono text-[12px]`} {...rest}>
                  {children}
                </code>
              );
            }
            return (
              <code className="rounded bg-surface-800 px-1 py-0.5 font-mono text-[12px] text-gray-200" {...rest}>
                {children}
              </code>
            );
          },
          pre: (props) => (
            <pre
              className="my-2 overflow-x-auto rounded border border-gray-800 bg-surface-950 p-3 font-mono text-[12px] leading-5 text-gray-200"
              {...props}
            />
          ),
          table: (props) => (
            <div className="my-2 overflow-x-auto">
              <table className="min-w-full border-collapse text-left text-xs" {...props} />
            </div>
          ),
          th: (props) => <th className="border border-gray-700 px-2 py-1 font-semibold text-gray-200" {...props} />,
          td: (props) => <td className="border border-gray-800 px-2 py-1 align-top text-gray-300" {...props} />,
          strong: (props) => <strong className="font-semibold text-gray-100" {...props} />,
          em: (props) => <em className="italic text-gray-200" {...props} />,
        }}
      >
        {value}
      </Markdown>
    </div>
  );
}
